import type {
  IncidentWorkItemWithContext,
  UserIdUnbranded,
} from "@thalamos/vision-common";
import {
  ParticipantRoleSchema,
  ParticipantStatusSchema,
} from "@thalamos/vision-common";

/**
 * Determines whether a given user is actively involved in an incident.
 *
 * If a user has requested an ownership transfer, or if they are a participant
 * but other participants have been transferred while they are not the owner,
 * they are not considered part of the incident.
 *
 * @param {IncidentWorkItemWithContext} incident - The incident containing participants.
 * @param {UserIdUnbranded | undefined} userId - The ID of the user to check.
 * @returns {boolean} - Returns `true` if the user is actively involved in the incident, otherwise `false`.
 */
export const isUserActiveInIncident = (
  { participants }: IncidentWorkItemWithContext,
  userId: UserIdUnbranded | undefined,
): boolean => {
  // If there's no user ID, they cannot own or be part of the incident
  if (!userId) return false;

  // Find the participant entry for the current user
  const currentUser = participants.find((p) => p.participantId === userId);
  if (!currentUser) return false; // If the user is not a participant, return false

  // Find the participant who is the owner of the incident
  const owner = participants.find(
    (p) => p.role === ParticipantRoleSchema.enum.owner,
  );

  // Check if the current user is the owner but has requested a transfer
  const isOwnerRequestingTransfer =
    owner?.participantId === userId &&
    owner.status === ParticipantStatusSchema.enum.requestedTransfer;
  if (isOwnerRequestingTransfer) return false; // If so, they no longer "own" the incident

  // If the current user is a "pending owner", they should have potential access (accept or reject transfer)
  if (currentUser.role === ParticipantRoleSchema.enum.pendingOwner) return true;

  // Check if any participants have been transferred away from the incident
  const hasTransferredParticipants = participants.some(
    (p) => p.status === ParticipantStatusSchema.enum.transferred,
  );

  // If there are transferred participants AND the current user is not the owner, return false
  const isNonOwnerWithTransferredParticipants =
    hasTransferredParticipants && owner?.participantId !== userId;

  // If the user is an owner or no transfers affect their status, return true
  return !isNonOwnerWithTransferredParticipants;
};
